import React from "react";
import { Divider, IconButton, Typography, useMediaQuery, Box, useTheme } from "@mui/material";
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';

const PatientDetailsComponent = ({
    edit, handlePatientDetailsEditClick, pat_name, pat_age, pat_sex, visitNumber, visitDate, pat_id, pat_diabetes,
    diabetes_duration_years, diabetes_duration_months, 
    visual_acuity_distant_left, visual_acuity_distant_right, 
    visual_acuity_near_left, visual_acuity_near_right,
    visual_acuity_left, visual_acuity_right, vision_issues // Backward compatibility
}) => {
    // Format vision issues - returns object with distant and near separately
    const formatVisionIssues = () => {
        // Try new structure first
        const distantLeft = visual_acuity_distant_left?.trim() || '';
        const distantRight = visual_acuity_distant_right?.trim() || '';
        const nearLeft = visual_acuity_near_left?.trim() || '';
        const nearRight = visual_acuity_near_right?.trim() || '';
        
        if (distantLeft || distantRight || nearLeft || nearRight) {
            const result = { distant: '', near: '' };
            if (distantLeft || distantRight) {
                const distantParts = [];
                if (distantLeft) distantParts.push(`${distantLeft} Left`);
                if (distantRight) distantParts.push(`${distantRight} Right`);
                result.distant = `Distant Vision - ${distantParts.join(', ')}`;
            }
            if (nearLeft || nearRight) {
                const nearParts = [];
                if (nearLeft) nearParts.push(`${nearLeft} Left`);
                if (nearRight) nearParts.push(`${nearRight} Right`);
                result.near = `Near Vision - ${nearParts.join(', ')}`;
            }
            return result;
        }
        
        // Fallback to old structure
        const left = visual_acuity_left?.trim() || '';
        const right = visual_acuity_right?.trim() || '';
        if (left && right) {
            return { distant: `Left: ${left}, Right: ${right}`, near: '' };
        } else if (left) {
            return { distant: `Left: ${left}`, near: '' };
        } else if (right) {
            return { distant: `Right: ${right}`, near: '' };
        } else if (vision_issues?.trim()) {
            return { distant: vision_issues, near: '' };
        }
        return { distant: 'Unknown', near: '' };
    };

    // Format diabetes duration
    // Format: "Yes(12years, 2months)" - with comma
    const formatDiabetesDuration = () => {
        if (pat_diabetes === "Yes" && (diabetes_duration_years || diabetes_duration_months)) {
            const years = diabetes_duration_years || 0;
            const months = diabetes_duration_months || 0;
            const parts = [];
            if (years) {
                parts.push(`${years}year${years > 1 ? 's' : ''}`);
            }
            if (months) {
                parts.push(`${months}month${months > 1 ? 's' : ''}`);
            }
            if (parts.length > 0) {
                return `(${parts.join(', ')})`;
            }
        }
        return null;
    };

    const visionIssues = formatVisionIssues();
    const diabetesDurationText = formatDiabetesDuration();
    // const isMobile = useMediaQuery('(max-width:600px)'); // Adjust the breakpoint as needed
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));


    return (
        <>
            <div style={{ display: "inline-flex", padding: "0 5%" }}>
                <Typography fontWeight={400} fontSize="28px" color="#0D1E83" textTransform="capitalize">{pat_id}</Typography>
                {edit ? <IconButton onClick={handlePatientDetailsEditClick}>
                    <EditOutlinedIcon fontSize="small" sx={{ color: "#0D1E83" }} />
                </IconButton> : null}
            </div>
            
            <div style={{ display: "flex", justifyContent: "space-between", padding: "0 5%" }}>
                {
                    !isMobile ? (
                        <div style={{width: "100%"}}>
                    
                        {/* Age, Gender, Visit No. + Visit Date */}
                        <div style={{ display: "flex", justifyContent: "space-between" }}>
                            <Typography textTransform="capitalize" fontWeight={400} fontSize="14px" color="#0D1E83">
                                {`${pat_age} Y | ${pat_sex} | Visit No. ${visitNumber==null || visitNumber==='N/A'? 'N/A': visitNumber}`}
                            </Typography>
                            <Typography fontSize="14px" fontWeight={600} color="#0D1E83">
                                Visit Date: {visitDate}
                            </Typography>
                        </div>
                    
                        <Divider sx={{ background: "rgba(13, 30, 131, 0.22)", margin: "12px 0" }} />
                    
                        {/* Status Info - Left: Best Corrected Visual Acuity, Right: Diabetes */}
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                            {/* Left Side: Best Corrected Visual Acuity */}
                            <div style={{ flex: "0 0 auto", maxWidth: "50%" }}>
                                <Typography fontSize="14px" fontWeight={600} color="#2C2C2C" sx={{ marginBottom: "8px" }}>
                                    Best Corrected Visual Acuity
                                </Typography>
                                {visionIssues.distant && (
                                    <Typography fontSize="14px" fontWeight={400} color="#2C2C2C" sx={{ marginBottom: visionIssues.near ? "4px" : 0 }}>
                                        {visionIssues.distant}
                                    </Typography>
                                )}
                                {visionIssues.near && (
                                    <Typography fontSize="14px" fontWeight={400} color="#2C2C2C">
                                        {visionIssues.near}
                                    </Typography>
                                )}
                            </div>
                            
                            {/* Right Side: Diabetes - Aligned to the right */}
                            <div style={{ flex: "0 0 auto", textAlign: "right", marginLeft: "auto" }}>
                                <Typography fontSize="14px" fontWeight={600} color="#2C2C2C" sx={{ marginBottom: "8px" }}>
                                    Diabetes
                                </Typography>
                                <Typography fontSize="14px" fontWeight={400} color="#2C2C2C">
                                    {pat_diabetes}{diabetesDurationText}
                                </Typography>
                            </div>
                        </div>
                    </div>                    
                        
                    ) : (
                        <div style={{width: "100%"}}>
                                {/* Mobile: Age, Gender, Visit No. + Visit Date - stacked like desktop */}
                                <div style={{ marginBottom: "12px" }}>
                                    <Typography textTransform="capitalize" fontWeight={400} fontSize="14px" color="rgba(0, 0, 0, 0.46)" sx={{ marginBottom: "4px" }}>
                                        {`${pat_age} Y | ${pat_sex} | Visit No. ${visitNumber==null || visitNumber==='N/A'? 'N/A': visitNumber}`}
                                    </Typography>
                                    <Typography fontSize="14px" fontWeight={400} color="rgba(0, 0, 0, 0.46)">
                                        Visit Date: {visitDate}
                                    </Typography>
                                </div>
                                <Divider sx={{ background: "rgba(13, 30, 131, 0.22)", margin: "12px 0" }} />
                                <div style={{ marginBottom: "12px" }}>
                                    <Typography fontSize="14px" fontWeight={600} color="#2C2C2C" sx={{ marginBottom: "4px" }}>
                                        Best Corrected Visual Acuity
                                    </Typography>
                                    {visionIssues.distant && (
                                        <Typography fontSize="14px" fontWeight={400} color="#2C2C2C" sx={{ marginBottom: visionIssues.near ? "4px" : 0 }}>
                                            {visionIssues.distant}
                                        </Typography>
                                    )}
                                    {visionIssues.near && (
                                        <Typography fontSize="14px" fontWeight={400} color="#2C2C2C">
                                            {visionIssues.near}
                                        </Typography>
                                    )}
                                </div>
                                <div>
                                    <Typography fontSize="14px" fontWeight={600} color="#2C2C2C" sx={{ marginBottom: "4px" }}>
                                        Diabetes
                                    </Typography>
                                    <Typography fontSize="14px" fontWeight={400} color="#2C2C2C">
                                        {pat_diabetes}{diabetesDurationText}
                                    </Typography>
                                </div>
                        </div>
                        
                    )
                }
            </div>
            
            
        </>
    )
}

export default PatientDetailsComponent;